import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Portal, useEscapeKey, useFocusTrap, useScrollLock } from '@/ui/kit'
import { cn, nextId } from './internal/utils'
import { BORDER, RADIUS_MD } from './internal/presets'
import Button from './Button'
import { confirm, destroyAll, error, info, success, warning } from './internal/modalConfirm'

/**
 * antd Modal. Keeps the dialog mounted once opened unless `destroyOnClose`, which
 * is what forms inside modals rely on. Focus is trapped and restored on close.
 */
const Modal = ({
  open,
  visible,
  title,
  onOk,
  onCancel,
  okText = 'OK',
  cancelText = 'Annuler',
  okType = 'primary',
  okButtonProps,
  cancelButtonProps,
  confirmLoading = false,
  footer,
  closable = true,
  closeIcon,
  maskClosable = true,
  keyboard = true,
  centered = false,
  width = 520,
  zIndex,
  destroyOnClose = false,
  afterClose,
  className,
  rootClassName,
  style,
  bodyStyle,
  styles,
  wrapClassName,
  forceRender = false,
  children,
  ...rest
}) => {
  const isOpen = Boolean(open ?? visible)
  const dialogRef = useRef(null)
  const [hasOpened, setHasOpened] = useState(isOpen || forceRender)
  const titleId = useRef(nextId('modal-title')).current

  useEffect(() => {
    if (isOpen) setHasOpened(true)
  }, [isOpen])

  useEffect(() => {
    if (isOpen || !hasOpened) return
    afterClose?.()
  }, [afterClose, hasOpened, isOpen])

  useScrollLock(isOpen)
  useFocusTrap(dialogRef, isOpen)
  useEscapeKey(() => onCancel?.(), isOpen && keyboard)

  if (!hasOpened) return null
  if (!isOpen && destroyOnClose) return null

  const resolvedBodyStyle = styles?.body || bodyStyle

  const defaultFooter = (
    <>
      <Button onClick={onCancel} {...cancelButtonProps}>
        {cancelText}
      </Button>
      <Button
        type={okType === 'danger' ? 'primary' : okType}
        danger={okType === 'danger'}
        loading={confirmLoading}
        onClick={onOk}
        {...okButtonProps}
      >
        {okText}
      </Button>
    </>
  )

  return (
    <Portal>
      <div
        className={cn('ui-modal-root', rootClassName)}
        style={{ display: isOpen ? undefined : 'none', zIndex: zIndex ?? 'var(--z-modal, 2000)', position: 'fixed', inset: 0 }}
      >
        <div
          className="ui-modal-mask fixed inset-0 bg-[var(--overlay-bg)]"
          onClick={maskClosable ? () => onCancel?.() : undefined}
          role="presentation"
        />
        <div
          className={cn(
            'ui-modal-wrap fixed inset-0 overflow-y-auto p-4',
            centered ? 'flex items-center justify-center' : 'flex justify-center pt-[100px]',
            wrapClassName
          )}
          style={{ pointerEvents: 'none' }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            tabIndex={-1}
            className={cn(
              'ui-modal relative my-auto w-full bg-[var(--surface-card)] shadow-[var(--shadow-lg)]',
              RADIUS_MD,
              className
            )}
            style={{ maxWidth: typeof width === 'number' ? `${width}px` : width, pointerEvents: 'auto', ...style }}
            {...rest}
          >
            {closable ? (
              <button
                type="button"
                aria-label="Fermer"
                onClick={() => onCancel?.()}
                className="ui-modal-close absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full text-[var(--gray-600)] hover:bg-[var(--gray-100)] hover:text-[var(--text-body)]"
              >
                {closeIcon ?? <X size={18} aria-hidden="true" />}
              </button>
            ) : null}

            {title ? (
              <div className={cn('ui-modal-header border-b px-4 py-3 pr-14', BORDER)}>
                <h2 id={titleId} className="ui-modal-title m-0 text-[length:var(--font-size-sm)] font-semibold text-[var(--foreground)]">
                  {title}
                </h2>
              </div>
            ) : null}

            <div className="ui-modal-body px-4 py-4" style={resolvedBodyStyle}>
              {children}
            </div>

            {footer === null ? null : (
              <div className={cn('ui-modal-footer flex items-center justify-end gap-2 border-t px-4 py-3', BORDER)}>
                {footer ?? defaultFooter}
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  )
}

Modal.confirm = confirm
Modal.info = info
Modal.success = success
Modal.error = error
Modal.warning = warning
Modal.warn = warning
Modal.destroyAll = destroyAll
/** antd's hook API returns [instance, contextHolder]; the holder is a no-op here. */
Modal.useModal = () => [
  { confirm, info, success, error, warning },
  null,
]

export default Modal
